import React, { ReactNode } from 'react';
import pdfElementarySchool from '@/assets/pdfs/Elementary-School-Guide.pdf';
import pdfMiddleSchool     from '@/assets/pdfs/Middle-School-Guide.pdf';
import pdfHighSchool       from '@/assets/pdfs/High-School-Guide.pdf';
import imgElementary       from '@/assets/images/survival_elementary.svg';
import imgMiddle           from '@/assets/images/survival_middle.svg';
import imgHigh             from '@/assets/images/survival_high.svg';
import sampleLessonImg     from '@/assets/images/sample_lesson_card_img.svg';
import timeImg             from '@/assets/images/time-1-3.svg';
import timeImgElm          from '@/assets/images/time-4-6.svg';

export type Grade = string;
export type Source = 'survival' | 'verticalCardsLesson' | 'section-school-content' | 'section-1-getting-started';

interface ContentByGrade {
  header: string;
  description: string | ReactNode;
  media?: string;
  pdfLinkUrl?: string;
  buttonLabel?: string;
  vidyardUuid?: string;
  additionalContent?: string | ReactNode;
}

interface TimeCardContent {
  timeDesc: string;
  timeRangeHeader: string;
  media: string;
}

// NavigatingK12School Screen Content
export const survivalSectionContent: Record<'ELEMENTARY' | 'MIDDLE' | 'HIGH', ContentByGrade> = {
  ELEMENTARY: {
    header: 'Survival Guides for Learning Online',
    description: (
        <>
        Starting something new can feel a little overwhelming at first, but you'll soon get the hang of what online learning is like. For a head start, check out our survival guide before jumping into your new school environment. 
        </>
    ),
    pdfLinkUrl: pdfElementarySchool,
    media: imgElementary,
    buttonLabel: 'Download the Survival Guide',
  },
  MIDDLE: {
    header: 'Survival Guides for Learning Online',
    description: (
    <>
        Starting something new can feel a little overwhelming at first, but you'll soon get the hang of what online learning is like. For a head start, check out our survival guide before jumping into your new school environment. 

    </>
    ),
    pdfLinkUrl: pdfMiddleSchool,
    media: imgMiddle,
  },
  HIGH: {
    header: 'Survival Guides for Learning Online',
    description: (
      <>
        Starting something new can feel a little overwhelming at first, but you'll soon get the hang of what online learning is like. For a head start, check out our survival guide before jumping into your new school environment. 
      </>
    ),
    pdfLinkUrl: pdfHighSchool,
    media: imgHigh,
  },
};
const sampleLessonSectionContent: Record<'ELEMENTARY' | 'MIDDLE' | 'HIGH', ContentByGrade> = {
  ELEMENTARY: {
    header: 'Check Out a Sample Lesson',
    description: <>
      <span>Want to know what a course is like? Try out a sample lesson!</span>
      <br /> <br />
      <span>Get excited for 5th Grade Earth, Life, and Physical Science! Try the interactive lessons here.</span>
    </>,
    media: sampleLessonImg,
    pdfLinkUrl: "https://learnx-svc.k12.com/learnx-svc/getIndex/token/calmsToken/conceptId/1130925/conceptType/ACTIVITY?documentId=5a03377b02d1d0a68784c62d#/activity/1130925/page/1"
  },
  MIDDLE: {
    header: 'Check Out a Sample Lesson',
    description: <>
      <span>Want to know what a course is like? Try out a sample lesson! </span>
      <br /> <br />
      <span>Delve into past times and civilizations with these middle school World History lessons.</span>
    </>,
    media: sampleLessonImg,
    pdfLinkUrl: "https://learnx-svc.k12.com/learnx-svc/getIndex/token/calmsToken/conceptId/1131566/conceptType/ACTIVITY?documentId=5a03377b02d1d0a68784c62d#/activity/1131566/page/1"
  },
  HIGH: {
    header: 'Check Out a Sample Lesson',
    description: <>
      <span>Want to know what a course is like? Try out a sample lesson! </span>
      <br /> <br />
      <span>Mix it up in our interactive high school chemistry course sample! </span>
    </>,
    media: sampleLessonImg,
    pdfLinkUrl: "https://learnx-svc.k12.com/learnx-svc/getIndex/token/calmsToken/conceptId/1131040/conceptType/ACTIVITY?documentId=5a03377b02d1d0a68784c62d#/activity/1131040/page/1"
  },
};
export const isMiddleOrHighSchool = (gradeId?: string): boolean => {
  if (!gradeId) return false;

  const gradeRange = getGradeRange(gradeId);
  return gradeRange === 'MIDDLE' || gradeRange === 'HIGH';
};
//End NavigatingK12School


// BeingLearningCoach Screen Content
export const lcRoleSchoolSectionContent: Record<'ELEMENTARY' | 'MIDDLE' | 'HIGH', ContentByGrade> = {
  ELEMENTARY: {
    header: "Your day as {{studentName}}'s learning coach",
    description: (
    <>
    <div>
      For younger students, you'll be hands-on and all in for most of the school day, making sure your student attends live online classes with their teachers. 
      You'll also organize course materials, help them complete lessons, and teach core skills. 
      You can find fun ways to include engaging offline activities, too—like trips to the museum or zoo.
      <br /><br />
      Want to see how it's done? Listen as Amanda, a veteran Learning Coach, shares her best advice for managing your new role.
    </div>
    </>
    ),
    vidyardUuid: "3eQ9H3UucpScJcmnMu4Yxq",
    additionalContent: (
        <>
        <div className="content-block">
        Here are her highlights:
        <ul className="list-block">
          <li>
            <strong>Plan Your Student's Day</strong><br />
            Find a method that works for you in scheduling lessons and managing your time.
          </li>
          <li>
            <strong>Lesson Prep and Grading</strong><br />
            Use your student's independent work time to prep anything they may need for the next lesson and to enter grades or attendance.
          </li>
          <li>
            <strong>Enjoy the Process</strong><br />
            As a Learning Coach in the early grades, you'll be there for all your child's discoveries. Take time to enjoy and celebrate their growth.
          </li>
          <li>
            <strong>Get Teacher Support</strong><br />
            Your teacher is there to support you, whether you need help organizing lessons or your child needs extra instruction, don't hesitate to reach out.
          </li>
        </ul>

        </div>
        </>
    ),
  },
  MIDDLE: {
    header: "Your day as {{studentName}}'s learning coach",
    description: (
    <>
    <div>
      As your student progresses to middle school, you'll continue to ensure they participate in live classes, redirecting them back to schoolwork when distractions arise. You'll actively support your child's growth toward independent learning and work to develop your student's time management skills.
      <br /><br />
      Hear from Amanda on being the Learning Coach of middle schoolers and  learn how she adapted her strategies to help her children succeed.
    </div>
    </>
    ),
    vidyardUuid: "f8nChd9S1v814txmAM7CyB",
    additionalContent: (
      <>
        <div className="content-block">
          <ul className="list-block">
            <li>
              <strong>Help Your Student Organize Their Day</strong><br />
              Model good time management by helping your student keep up with their schedule.
            </li>
            <li>
              <strong>Increased Class Time</strong><br />
              Students will have more subject-specific teachers and additional class sessions, so they will need less help from you to learn their material.
            </li>
            <li>
              <strong>Keep Track of Schoolwork</strong><br />
              While you won’t need to sit with your student through their lessons, make sure you keep informed of how much time they’re spending on schoolwork.
            </li>
            <li>
              <strong>Get Teacher Support</strong><br />
              Assist your student in advocating for themselves when they have questions or need extra support. Teachers are glad to help!
            </li>
          </ul>
        </div>
      </>
    ),
  },
  HIGH: {
    header: "Your day as {{studentName}}'s learning coach",
    description: (
    <>
    <div>
      As the Learning Coach of a high schooler, you’ll support your child  in becoming a fully independent learner.You’ll help them sharpen their  time management skills, track their class attendance, and motivate them  to prioritize their schoolwork within their busy teenage years.
      <br /><br />
      Amanda can tell you all about what she learned as the Learning Coach in support of her teens! Here she shares her best advice.
    </div>
    </>
    ),
    vidyardUuid: "vXjVZbVSWiUp2Viy6kvHFC",
    additionalContent: (
      <>
        <div className="content-block">
          <ul className="list-block">
            <li>
              <strong>Know Your Student’s Schedule</strong><br />
              Help your high schooler manage their busy schedule by checking in and reminding them about due dates and Class Connects.
            </li>
            <li>
              <strong>Support Independent Learning</strong><br />
              Students at the high school level are responsible for their own work, while Learning Coaches take on more of a support role. Let your student take control of their learning.
            </li>
            <li>
              <strong>Check the Gradebook</strong><br />
              As a Learning Coach, you have access to monitor grades and lesson content. Ask questions to make sure students are making progress and understanding content.
            </li>
            <li>
              <strong>Get Teacher Support</strong><br />
              Although teachers will work closely with your student, you can always feel free to reach out to them directly if you have any questions or concerns.
            </li>
          </ul>
        </div>
      </>
    ),
  },
};
const timeCommitmentSectionContent: Record<'ELEMENTARY' | 'MIDDLE' | 'HIGH', TimeCardContent> = {
  ELEMENTARY: {
    timeDesc: "For grades K-5, Learning Coaches play a hands-on role, guiding students through lessons, keeping them engaged, and building the foundation for future success. ",
    timeRangeHeader: "Average Daily Elementary School Time Commitment: 4-6 hours",
    media: timeImgElm 
  },
  MIDDLE: {
    timeDesc: "Middle school students begin to grow as independent learners, and the role of Learning Coach often shifts to include modeling important life skills like organization and time management. ",
    timeRangeHeader: " Average Daily Middle School Time Commitment: 1-3 hours",
    media: timeImg
  },
  HIGH: {
    timeDesc: "High school is a busy time of exploring extracurricular activities, electives, and options for college and career. Help keep students on track and cheer them on! ",
    timeRangeHeader: "Average Daily High School Time Commitment: 1-3 hours",
    media: timeImg
  },
};

// GradeRange Logic
const getGradeRange = (grade: Grade): 'ELEMENTARY' | 'MIDDLE' | 'HIGH' | null => {
  const formatted = grade.trim().toUpperCase();

  // Specific handling for K–5 
  const elementaryGrades = ['K', '0', '1', '2', '3', '4', '5'];
  if (elementaryGrades.includes(formatted)) return 'ELEMENTARY';
  const gradeNum = Number(formatted);
  // Range 6–8 & 9-12 
  if (!Number.isNaN(gradeNum)) {
    if (gradeNum >= 6 && gradeNum <= 8) return 'MIDDLE';
    if (gradeNum >= 9 && gradeNum <= 12) return 'HIGH';
  }

  return null;
};
// Reusable Functions for Content (With Grade-Range)
export function getContentByGrade(grade: Grade, source: Source): ContentByGrade | null {
  const gradeKey = getGradeRange(grade);
  if (!gradeKey) return null;

  switch (source) {
    case 'survival':
      return survivalSectionContent[gradeKey];
    case 'verticalCardsLesson':
      return sampleLessonSectionContent[gradeKey];
    case 'section-school-content':
      return lcRoleSchoolSectionContent[gradeKey];
    default:
      return null;
  }
}
// Functions for time commitment (With Grade-Range)
export function getTimeCardContentByGrade(grade: Grade): TimeCardContent | null {
  const gradeKey = getGradeRange(grade);
  if (!gradeKey) return null;

  return timeCommitmentSectionContent[gradeKey];
}
